import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CustomInput } from 'reactstrap';

import {
  InvestorActiveStoDocument,
  useInvestorActiveStoQuery,
  useInvestorAppDataQuery,
  useInvestorBuyPropertyUpdateMutation,
  useMeQuery,
  useInvestorStoDetailQuery,
  LogAction,
  useCreateLogMutation,
} from 'services/apollo';

import { Col, Row, Loading, Button, CenteredCol, FormGroup, BsSwal } from 'atoms';
import MessageBox from 'pages/chat/components/MessageBox';
import NdaModal from 'pages/nda/NdaModal';
import { ThemeType } from 'lib/themes/themeTypes';
import { useTheme } from 'styled-components';
import { canSeeNDAData, canSeeNDADataRequired, showBuyButton } from 'services/core/helpers';
import PropertyGallery from './components/PropertyGallery';
import PropertyDocuments from './components/PropertyDocuments';
import MetaChart from './components/MetaChart';
import MetaContent from './components/MetaContent';
import DynamicContent from './components/DynamicContent';
import MetaFilterProperties from './components/MetaFilterProperties';

const ignoredKeys = ['left_sidebar', 'left_sidebar_chart'];

const MarketSpacePropertyDetail: React.FC = () => {
  const stoId = Number(useParams<{ _id: string }>()?._id);
  const history = useHistory();
  const { t } = useTranslation();

  const [isOpen, setOpen] = useState(false);
  const [isInterested, setIsInterested] = useState(false);
  const { data: appData, loading } = useInvestorAppDataQuery();
  const { data: investorSto, loading: investorStoLoading } = useInvestorActiveStoQuery({
    variables: { _id: stoId },
    fetchPolicy: 'network-only',
  });

  const { data: stoDetails, loading: loadStoDetails } = useInvestorStoDetailQuery({
    variables: { _id: stoId },
  });
  const { data: meData, loading: meLoading } = useMeQuery({ fetchPolicy: 'network-only' });
  const [updateInvestorBuyProperty] = useInvestorBuyPropertyUpdateMutation({
    refetchQueries: [{ query: InvestorActiveStoDocument }],
  });
  const [investorAccessedDealRoom] = useCreateLogMutation();
  const theme: ThemeType = useTheme() as ThemeType;

  useEffect(() => {
    if (investorSto?.investorSto?.isBuyStoEnabled !== undefined) {
      setIsInterested(investorSto?.investorSto?.isBuyStoEnabled);
    }
  }, [investorSto?.investorSto]);

  if (loadStoDetails || !stoDetails || investorStoLoading || meLoading) {
    return <Loading />;
  }

  const { findSto: sto } = stoDetails;

  if (!sto || loading || !appData || !meData || !meData.investorUser) {
    return <Loading />;
  }

  const { investor } = meData.investorUser;
  const {
    areSTOHostnamesEnabled: areHostNamesEnabled,
    isBuyStoWhitelistPerInvestorEnabled,
    isInvestorDisassociationSwitchEnabled,
    isMetakeysFilterTagsDisplayEnabled,
    isRegDRegSSeparationEnabled,
  } = appData.investorAppParameters;

  const NDACheckData: canSeeNDADataRequired = {
    isStoNDAFlowEnabled: sto.isNDAFlowEnabled,
    appData: appData.investorAppParameters,
    investorCountry: investor.country ?? '',
    isNDASigned: investorSto?.investorSto.isNDASigned,
    isBuyButtonEnabled: sto.isBuyButtonEnabled,
    isBuyStoEnabled: investorSto?.investorSto.isBuyStoEnabled,
  };

  const buy = () => {
    if (!canSeeNDAData(NDACheckData) && !isRegDRegSSeparationEnabled) {
      setOpen(true);
    } else if (sto.stoDetailsInvestNowLink && sto.stoDetailsInvestNowLink.length > 0) {
      // @remark Log access to deal room
      investorAccessedDealRoom({
        variables: {
          stoId: sto.ID,
          activityType: LogAction.InvestorAccessedDealRoom,
        },
      });
      window.location.href = sto.stoDetailsInvestNowLink;
    } else {
      history.push(`/investor/buy-property/${sto.ID}`);
    }
  };

  const detail = [
    {
      key: 'project_overview',
      value: sto.fullDetails,
      stoID: sto.ID,
      order: 0,
      display: true,
      displayClosedProp: true,
    },
  ];
  const metaSidebar = sto.meta.find((x) => x.key === 'left_sidebar');
  const isLeft = !!metaSidebar?.value;

  const metaList = sto.meta.filter((meta) => !ignoredKeys.includes(meta.key));
  const pictureUrl =
    sto.ID > 0 && !areHostNamesEnabled
      ? `https://${sto.stolinkfull.substring(sto.stolinkfull.indexOf('.') + 1)}`
      : sto.stolinkfull;

  const onChangeBuyStoEnabled = async () => {
    try {
      await updateInvestorBuyProperty({
        variables: {
          stoID: stoId,
          isEnabled: false,
        },
      });
      setIsInterested(false);
    } catch (err) {
      const errorComponents: string[] = (err as Error)?.message.split('/');
      BsSwal.fire({
        title: t(errorComponents[0]),
        icon: 'error',
        cancelButtonText: t('Cancel'),
      });
    }
  };

  const changeBuyButtonText = () => {
    if (!canSeeNDAData(NDACheckData) && !isRegDRegSSeparationEnabled) {
      return t('ActiveProperty-Buy-nda-not-signed');
    }
    return t('MarketSpacePropertyDetails-button-investNow');
  };

  const themeStyling = {
    color: theme.fontColorRightPanel,
    fontWeight: theme.fontWeightRightPanel,
  };

  return (
    <div className="content">
      <Row>
        <Col md={8} style={themeStyling}>
          <h1>{sto.title}</h1>
        </Col>
        <hr />
        <CenteredCol>
          {showBuyButton(NDACheckData) ? (
            <Button wmin="200px" hmin="130px" className="m-1 text-nowrap" onClick={buy}>
              {changeBuyButtonText()}
            </Button>
          ) : null}
        </CenteredCol>
        <CenteredCol>
          {isBuyStoWhitelistPerInvestorEnabled && isInvestorDisassociationSwitchEnabled && isInterested ? (
            <FormGroup switch>
              <CustomInput
                id="is-interested"
                type="switch"
                onChange={onChangeBuyStoEnabled}
                label={t('MarketSpacePropertyDetails-InvestorDisassociationSwitch-Label')}
                checked={isInterested}
                disabled={!isInterested}
              />
            </FormGroup>
          ) : null}
        </CenteredCol>
      </Row>
      <Row>
        <Col md={isLeft ? 8 : 'auto'}>
          {sto.images.length > 0 && (
            <PropertyGallery
              classNames={!isLeft ? 'w-100 h-50' : ''}
              logo={`${pictureUrl}/img/stologo/${sto.logo}`}
              images={sto.images}
            />
          )}
          <MetaContent meta={detail} name="project_overview" />
          <DynamicContent meta={metaList} isBuyEnabled={sto.isBuyButtonEnabled} />
        </Col>
        {isLeft ? (
          <Col md={4} style={themeStyling}>
            {isMetakeysFilterTagsDisplayEnabled && (
              <div className="mt-4">
                <MetaFilterProperties metaContent={sto.meta} />
              </div>
            )}
            <div className="mt-4">
              <b>Details</b>
            </div>
            <p>{sto.details}</p>

            <PropertyDocuments documents={sto.documents || []} />
            <MetaChart meta={sto.meta} name="left_sidebar_chart" />
            <MessageBox stoID={sto.ID} investorID={investor.ID} />
          </Col>
        ) : null}
      </Row>
      <NdaModal stoID={sto.ID} isOpen={isOpen} setOpen={setOpen} />
    </div>
  );
};

export default MarketSpacePropertyDetail;
